import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Trash2, Plus } from "lucide-react";
import api from "@/lib/api";
import { useListUids } from "@/lib/hooks";

/**
 * Reusable admin CRUD list for taxonomy/entity collections.
 * fields: [{key, label, type?, placeholder?, options?, required?}]
 */
export default function AdminEntityList({ title, endpoint, listEndpoint, listFn, fields, testIdBase, itemLabel = (i) => i.name || i.title }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // form state
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const r = listFn ? await listFn() : await api.get(listEndpoint || endpoint);
    setItems(r.data);
  };
  useEffect(() => { load(); }, []);

  const startNew = () => {
    const blank = {};
    fields.forEach((f) => (blank[f.key] = f.default ?? (f.type === "list" ? [] : "")));
    setEditing(blank);
  };
  const startEdit = (i) => setEditing({ ...i });

  const save = async () => {
    setSaving(true);
    try {
      if (editing.slug && items.find((x) => x.slug === editing.slug)) {
        // update
        await api.put(`${endpoint}/${editing.slug}`, editing);
        toast.success("Saved");
      } else {
        await api.post(endpoint, editing);
        toast.success("Created");
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error("Failed to save.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (slug) => {
    if (!confirm("Delete?")) return;
    await api.delete(`${endpoint}/${slug}`);
    toast.success("Deleted");
    load();
  };

  return (
    <div data-testid={`${testIdBase}-list`}>
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="eyebrow text-oc-coral mb-2">{title}</div>
          <h1 className="font-editorial text-4xl text-oc-ink">Manage {title.toLowerCase()}</h1>
        </div>
        <button
          data-testid={`${testIdBase}-new`}
          onClick={startNew}
          className="h-12 px-5 bg-oc-blue text-white font-ui text-xs font-bold uppercase tracking-widest flex items-center gap-2 hover:bg-oc-blue-hover"
        >
          <Plus className="w-4 h-4" /> New
        </button>
      </div>

      <div className="border border-oc-hairline">
        {items.map((i) => (
          <div key={i.slug} className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-oc-hairline last:border-0 items-center hover:bg-oc-sand/50">
            <div className="col-span-8">
              <button onClick={() => startEdit(i)} className="text-left font-ui font-semibold text-oc-ink hover:text-oc-blue">
                {itemLabel(i)}
              </button>
              <div className="font-ui text-xs text-oc-ink-muted mt-1">/{i.slug}</div>
            </div>
            <div className="col-span-4 flex items-center justify-end gap-3">
              <button onClick={() => startEdit(i)} className="font-ui text-xs font-bold uppercase tracking-widest text-oc-blue hover:text-oc-ink">Edit</button>
              <button onClick={() => remove(i.slug)} className="p-2 text-oc-coral hover:bg-oc-sand" aria-label="Delete">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && <div className="p-8 text-center font-ui text-sm text-oc-ink-muted">No entries yet.</div>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white w-full max-w-2xl max-h-[90vh] overflow-auto p-8" onClick={(e) => e.stopPropagation()}>
            <div className="eyebrow text-oc-coral mb-3">{title}</div>
            <h2 className="font-editorial text-3xl text-oc-ink mb-6">{editing.slug ? "Edit" : "New"}</h2>
            <div className="space-y-4">
              {fields.map((f) => (
                <FieldRenderer key={f.key} field={f} value={editing[f.key]} onChange={(v) => setEditing({ ...editing, [f.key]: v })} />
              ))}
            </div>
            <div className="mt-8 flex gap-3">
              <button
                data-testid={`${testIdBase}-save`}
                disabled={saving}
                onClick={save}
                className="h-11 px-5 bg-oc-blue text-white font-ui text-xs font-bold uppercase tracking-widest hover:bg-oc-blue-hover disabled:opacity-60"
              >
                {saving ? "Saving..." : "Save"}
              </button>
              <button onClick={() => setEditing(null)} className="h-11 px-5 border border-oc-ink font-ui text-xs font-bold uppercase tracking-widest">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function FieldRenderer({ field, value, onChange }) {
  if (field.type === "textarea") {
    return (
      <label className="block">
        <span className="eyebrow text-oc-ink-muted mb-2 block">{field.label}{field.required && " *"}</span>
        <textarea rows={field.rows || 3} value={value ?? ""} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 bg-white border border-oc-hairline focus:border-oc-ink outline-none font-ui text-sm resize-y" />
      </label>
    );
  }
  if (field.type === "list") {
    const arr = value || [];
    return <ListSubField label={field.label} items={arr} onChange={onChange} />;
  }
  return (
    <label className="block">
      <span className="eyebrow text-oc-ink-muted mb-2 block">{field.label}{field.required && " *"}</span>
      <input type={field.type || "text"} required={field.required} value={value ?? ""} onChange={(e) => onChange(e.target.value)} placeholder={field.placeholder} className="w-full h-11 px-3 bg-white border border-oc-hairline focus:border-oc-ink outline-none font-ui text-sm" />
    </label>
  );
}

function ListSubField({ label, items, onChange }) {
  const { keyOf, add, remove, update } = useListUids(items, onChange);
  return (
    <div>
      <span className="eyebrow text-oc-ink-muted mb-2 block">{label}</span>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={keyOf(i)} className="flex gap-2">
            <input value={it} onChange={(e) => update(i, e.target.value)} className="flex-1 h-10 px-3 bg-white border border-oc-hairline outline-none font-ui text-sm" />
            <button type="button" onClick={() => remove(i)} className="px-3 h-10 border border-oc-hairline text-oc-coral font-ui text-xs">Remove</button>
          </div>
        ))}
        <button type="button" onClick={() => add("")} className="h-10 px-4 border border-oc-ink font-ui text-xs font-bold uppercase tracking-widest">
          + Add
        </button>
      </div>
    </div>
  );
}
